// Runtime configuration loader for the legacy-vanilla frontend.
//
// Resolves backend endpoints at runtime so the same bundle can target
// dev / preview / production without rebuilding. Resolution priority:
//
//   1. `window.__APP_RUNTIME_CONFIG__` set by an inline <script> (deploy-time).
//   2. `runtime-config.json` fetched from the document origin.
//   3. Vite-style env vars (`VITE_API_BASE_URL`, `VITE_WS_URL`) injected at
//      build time when `import.meta.env` is available.
//   4. Same-origin fallback: derive both URLs from `location` so a backend
//      that serves the bundle at `/` works with no config file.
//
// Production deployments should ship a `runtime-config.json` containing
// only metadata (`frontendName`, `frontendVersion`, `protocolVersion`) —
// `apiBaseUrl` / `wsUrl` are filled in from the document origin. Hardcoded
// localhost URLs in this file would ship a broken bundle to real users.

const FALLBACK_API_PREFIX = "/api/v1";
const FALLBACK_WS_PATH = "/api/v1/live/ws";

let cached = null;
let pending = null;

function pickFromWindow() {
  if (typeof window === "undefined") return null;
  const w = window.__APP_RUNTIME_CONFIG__;
  if (w && typeof w === "object") return { ...w, _source: "window" };
  return null;
}

async function pickFromJsonFile() {
  if (typeof fetch === "undefined") return null;
  try {
    // `runtime-config.json` is the convention shipped in
    // `frontends/legacy-vanilla/public/`. Vite copies anything under
    // `public/` to the dev server root and to the build output, so the same
    // path works in dev, in production and in same-origin backend mode.
    const res = await fetch("runtime-config.json", { cache: "no-store" });
    if (!res.ok) return null;
    const data = await res.json();
    if (data && typeof data === "object") return { ...data, _source: "runtime-config.json" };
  } catch {
    // Silent: a missing file is the expected case in same-origin mode.
  }
  return null;
}

function pickFromViteEnv() {
  // `import.meta.env` only exists when bundled by Vite. In a plain backend
  // load this access is a syntax-safe no-op because we guard with try/catch.
  try {
    if (typeof import.meta === "undefined" || !import.meta.env) return null;
    const env = import.meta.env;
    const apiBaseUrl = env.VITE_API_BASE_URL;
    const wsUrl = env.VITE_WS_URL;
    if (!apiBaseUrl && !wsUrl) return null;
    return {
      apiBaseUrl,
      wsUrl,
      frontendName: env.VITE_FRONTEND_NAME,
      frontendVersion: env.VITE_FRONTEND_VERSION,
      protocolVersion: env.VITE_PROTOCOL_VERSION
        ? Number(env.VITE_PROTOCOL_VERSION)
        : undefined,
      _source: "vite-env",
    };
  } catch {
    return null;
  }
}

function sameOriginFallback() {
  if (typeof location === "undefined") {
    return {
      apiBaseUrl: `http://127.0.0.1:8000${FALLBACK_API_PREFIX}`,
      wsUrl: `ws://127.0.0.1:8000${FALLBACK_WS_PATH}`,
      _source: "fallback-default",
    };
  }
  const httpProto = location.protocol === "https:" ? "https:" : "http:";
  const wsProto = location.protocol === "https:" ? "wss:" : "ws:";
  return {
    apiBaseUrl: `${httpProto}//${location.host}${FALLBACK_API_PREFIX}`,
    wsUrl: `${wsProto}//${location.host}${FALLBACK_WS_PATH}`,
    _source: "same-origin",
  };
}

function trimTrailingSlash(value) {
  if (typeof value !== "string") return value;
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

function fillDefaults(partial) {
  const fallback = sameOriginFallback();
  return {
    apiBaseUrl: trimTrailingSlash(partial.apiBaseUrl || fallback.apiBaseUrl),
    wsUrl: partial.wsUrl || fallback.wsUrl,
    frontendName: partial.frontendName || "legacy-vanilla",
    frontendVersion: partial.frontendVersion || "1.0.0",
    protocolVersion:
      typeof partial.protocolVersion === "number" ? partial.protocolVersion : 1,
    _source: partial._source || fallback._source,
  };
}

export async function ensureRuntimeConfig() {
  if (cached) return cached;
  if (pending) return pending;

  pending = (async () => {
    const fromWindow = pickFromWindow();
    if (fromWindow) {
      cached = fillDefaults(fromWindow);
      return cached;
    }
    const fromJson = await pickFromJsonFile();
    if (fromJson) {
      cached = fillDefaults(fromJson);
      return cached;
    }
    const fromVite = pickFromViteEnv();
    if (fromVite) {
      cached = fillDefaults(fromVite);
      return cached;
    }
    cached = fillDefaults(sameOriginFallback());
    return cached;
  })();

  return pending;
}

export function getRuntimeConfig() {
  if (!cached) {
    throw new Error("runtime config not yet loaded; await ensureRuntimeConfig() first");
  }
  return cached;
}

// Test/internal helper: lets tests reset the cache between cases.
export function _resetRuntimeConfigForTest() {
  cached = null;
  pending = null;
}

// Pure helpers exposed for tests.
export const _internals = {
  trimTrailingSlash,
  fillDefaults,
  sameOriginFallback,
  FALLBACK_API_PREFIX,
  FALLBACK_WS_PATH,
};
